/**
 * Resolving a Missing Waste Line row by copying the e-Manifest line into
 * vendor data (the addVendorWasteLinesFromEManifest mutation). Mirrors that
 * resolver's eligibility so the dialog can say why Save is off.
 */
import type { Q1Answer, ResolveRow } from "./resolveWithValue.helpers";

export const UNPAIRED_WASTE_LINE_FIELD = "unpairedWasteLine";

type AdditionRow = Pick<
  ResolveRow,
  | "field"
  | "status"
  | "eManifestValue"
  | "vendorValue"
  | "documentDiscrepant"
  | "vendorWasteLinesMatched"
>;

/** The row stores its line numbers comma-joined ("3" or "3, 4"). */
function parseLineNumbers(value: string | null | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((n) => n.trim())
    .filter((n) => n.length > 0);
}

/** The addition is a single-row flow on a Missing Waste Line row. */
export function isVendorLineAdditionRow(rows: readonly Pick<ResolveRow, "field">[]): boolean {
  return rows.length === 1 && rows[0].field === UNPAIRED_WASTE_LINE_FIELD;
}

/** Picking e-Manifest on a Missing Waste Line row saves through the addition, not resolveDiscrepancies. */
export function isVendorLineAdditionSave(
  answer: Q1Answer | null,
  rows: readonly Pick<ResolveRow, "field">[]
): boolean {
  return answer === "EMANIFEST" && isVendorLineAdditionRow(rows);
}

/** Why the e-Manifest line can't be added to vendor data, or null when it can. */
export function vendorLineAdditionDisabledReason(row: AdditionRow): string | null {
  if (row.status !== "UNRESOLVED") return "This row is no longer Pending Review";
  // A vendor-only line means the two sides' line numbers may not name the same waste.
  if (row.vendorValue != null) {
    return "The vendor manifest has lines the e-Manifest lacks. Match the waste lines first.";
  }
  // A new line leaves the saved pairing incomplete, so the server discards it.
  if (row.vendorWasteLinesMatched === true) {
    return "A recorded waste line pairing applies to this manifest, so adding a line would discard it. Remove the pairing first.";
  }
  if (parseLineNumbers(row.eManifestValue).length === 0) return "No e-Manifest line to add";
  if (row.documentDiscrepant === true) return "The manifest document also disagrees on this row";
  return null;
}

/** "line 3", "lines 3, 4", or "the line" when the value names none. */
export function describeWasteLines(value: string | null | undefined): string {
  const lines = parseLineNumbers(value);
  if (lines.length === 0) return "the line";
  return `${lines.length === 1 ? "line" : "lines"} ${lines.join(", ")}`;
}

/** The Preview's Vendor update, e.g. "Add line 3 from e-Manifest". */
export function formatVendorLineAddition(eManifestValue: string | null | undefined): string {
  return `Add ${describeWasteLines(eManifestValue)} from e-Manifest`;
}
